import ArrowDown from "assets/icons/mentorship-plan-varint-b/arrow-down.svg";
import BlackScreen from "assets/icons/mentorship-plan-varint-b/black-screen.svg";
import GrayDotIcon from "assets/icons/mentorship-plan-varint-b/gray-dot-icon.svg";
import TopicFolderIcon from "assets/icons/mentorship-plan-varint-b/topic-folder-icon.svg";
import { AnimatePresence, motion } from "framer-motion";
import useTopic from "./use-topic";
import { MentorshipPlanTopicTransformed } from "../../../mentorship-curriculum/types/mentorship-plan";

type TopicProps = {
  subject: string;
  groupMentorTopicsBySubject: Record<string, MentorshipPlanTopicTransformed[]>;
  expandTopics: string[];
  toggleExpandTopics: (subjects: string[]) => void;
};

const Topic = ({
  subject,
  groupMentorTopicsBySubject,
  expandTopics,
  toggleExpandTopics,
}: TopicProps) => {
  const { topicsBySubject, monthsPresent, totalStudyMaterial, totalSubTopics } =
    useTopic({ subject, groupMentorTopicsBySubject });

  return (
    <div className="md:p-5 px-3 py-3.5 bg-white">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => toggleExpandTopics([subject])}
      >
        <div className="flex gap-4 items-center">
          <img src={TopicFolderIcon} alt="folder.svg" />
          <div className="space-y-2">
            <p className="text-[#0E121B] md:text-[15px] text-[13px] md:leading-5 leading-4 md:-tracking-[0.15px] -tracking-[0.26px] font-semibold">
              {subject}
            </p>
            <p className="text-[#525866] text-xs leading-4 md:block hidden">
              To Be Covered In:{" "}
              <span className="font-semibold">{monthsPresent}</span>
            </p>
            <div className="md:hidden flex items-center gap-x-1.5">
              <p className="text-[#717784] text-[11px] leading-4 font-medium -tracking-[0.165px]">
                {monthsPresent}
              </p>
              <img src={GrayDotIcon} alt="dot.svg" />
              <p className="text-[#D45B1E] text-[11px] leading-4 font-medium -tracking-[0.165px]">
                {totalSubTopics} Sub Topics
              </p>
              {totalStudyMaterial > 0 && (
                <>
                  <img src={GrayDotIcon} alt="dot.svg" />
                  <p className="text-[#4316CA] text-[11px] leading-4 font-medium -tracking-[0.165px]">
                    {totalStudyMaterial} Study Material
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-x-5 md:divide-x md:divide-[#E1E4EA]">
          <div className="md:flex items-center gap-x-3 hidden">
            {totalStudyMaterial > 0 && (
              <div className="px-1.5 py-[1px] bg-[#F4F1FD]">
                <p className="text-[#4316CA] text-[11px] leading-5 font-medium">
                  {totalStudyMaterial} Study Material
                </p>
              </div>
            )}
            <div className="px-1.5 py-[1px] bg-[#FFF1EB]">
              <p className="text-[#682F12] text-[11px] leading-5 font-medium">
                {totalSubTopics} Sub Topics
              </p>
            </div>
          </div>
          <div className="pl-5">
            <button className="w-6 h-6 border border-[#E1E4EA] flex items-center justify-center">
              <img
                src={ArrowDown}
                alt="arrow-down.svg"
                className={`${
                  expandTopics.includes(subject) ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {expandTopics.includes(subject) && (
          <motion.div
            className="overflow-hidden"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <div className="h-[1px] w-full bg-[#F2F2F2] md:my-6 my-5" />
            <div className="md:space-y-4 space-y-3.5 divide-y divide-[#F1F2F4]">
              {topicsBySubject.map((topic) => (
                <div
                  key={topic.recordId + topic.month}
                  className="flex items-center justify-between md:pt-4 pt-3.5 first:pt-0"
                >
                  <div className="flex items-start gap-x-2">
                    <div className="w-[18px] h-[18px] flex items-center justify-center">
                      <img
                        src={BlackScreen}
                        alt="black-screen.svg"
                        className="w-[16px] h-[16px]"
                      />
                    </div>
                    <div className="md:space-y-1.5">
                      <p className="md:text-[#2B303B] text-[#717784] md:text-sm text-[10px] md:font-medium md:-tracking-[0.084px] -tracking-[0.06px]">
                        {topic.topic}
                      </p>
                      <p className="text-[#99A0AE] text-[11px] leading-4 md:block hidden">
                        {topic.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-x-3 shrink-0">
                    {topic.topicAssignmentsLength > 0 && (
                      <div className="bg-[#EBF8FF] py-[3px] px-2.5 md:block hidden">
                        <p className="text-[#124B68] text-xs leading-4 font-medium">
                          {topic.topicAssignmentsLength} Assignment
                          {topic.topicAssignmentsLength > 1 ? "s" : ""}
                        </p>
                      </div>
                    )}
                    {topic.topicProjectsLength > 0 && (
                      <div className="bg-[#EBF8FF] py-[3px] px-2.5 md:block hidden">
                        <p className="text-[#124B68] text-xs leading-4 font-medium">
                          {topic.topicProjectsLength} Project
                          {topic.topicProjectsLength > 1 ? "s" : ""}
                        </p>
                      </div>
                    )}
                    {topic.topicResourcesLength > 0 && (
                      <div className="bg-[#EBF8FF] py-[3px] px-2.5 md:block hidden">
                        <p className="text-[#124B68] text-xs leading-4 font-medium">
                          {topic.topicResourcesLength} Resource
                          {topic.topicResourcesLength > 1 ? "s" : ""}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-x-0.5 md:py-[3px] py-[2px] md:pl-[3px] pl-[2px] md:pr-2.5 pr-2 border border-[#F2F2F2]">
                      <div className="md:w-4 md:h-4 w-3 h-3 flex items-center justify-center">
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${
                            topic.month === 1
                              ? "bg-[#1FC16B]"
                              : topic.month === 2
                              ? "bg-[#7D52F4]"
                              : topic.month === 3
                              ? "bg-[#F6B51E]"
                              : topic.month === 4
                              ? "bg-[#22D3BB]"
                              : topic.month === 5
                              ? "bg-[#FF8447]"
                              : "bg-[#18658b]"
                          }`}
                        />
                      </div>
                      <p className="text-[#0E121B] md:text-xs text-[10px] leading-4 font-medium">
                        Month {topic.month}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Topic;
